import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import { mergeArrayOfClassInstances } from "@bloglovin/vue-framework";
import ComponentName from "%/dynamic-component-editor/options/component-name";
import ComponentDescription from "%/dynamic-component-editor/options/component-description";
import DisplayName from "%/dynamic-component-editor/options/display-name";

import BaseOption from "%/dynamic-component-editor/classes/base-option";
import RequiredContexts from "%/dynamic-component-editor/options/required-contexts";
// Legacy entity classes are imported lazily inside `standardElementEntities`
// (via require) instead of at module top — see the comment there for the
// circular-import path that forced this.


const { createProcessedComponentRegistry } = require("%/dynamic-component-editor/helpers/processed-component-registry");

const elementVueComponentRegistry = createProcessedComponentRegistry();

export default class BaseDynamicComponent {

  // Legacy entity classes are require()d lazily here, not imported at
  // module top, so this file can finish evaluating without pulling
  // LegacyGeneralEntity into the cycle. The cycle:
  //
  //   base-dynamic-component.js (loading)
  //     → LegacyGeneralEntity (loading)
  //       → PageHeader.vue
  //         → page-header.js
  //           → base-dynamic-component.js  ← TDZ on BaseDynamicComponent
  //
  // With the top-level imports gone, base-dynamic-component.js completes
  // its module evaluation before PageHeader's `extends BaseDynamicComponent`
  // is reached, and `require()` inside the getter runs after both modules
  // have finished initializing.
  // XXX: NOT contexts. These are the STANDARD ELEMENTS -- the ones that do not
  // XXX: bind to a context at all and are always available on every DC. They
  // XXX: were called globalProvidesContexts and travelled the contexts path,
  // XXX: which is precisely why they got mistaken for a per-DC authored context
  // XXX: declaration and deleted, emptying every standard group in the element
  // XXX: panel. A DC's real contexts come from its stored root options; these
  // XXX: are framework-supplied and no author declares them.
  static get standardElementEntities() {
    return {
      "layout": require("%/dynamic-component-editor/entities/LegacyLayoutEntity").default,
      "general": require("%/dynamic-component-editor/entities/LegacyGeneralEntity").default,
      "text": require("%/dynamic-component-editor/entities/LegacyTextEntity").default,
      "payments": require("%/dynamic-component-editor/entities/LegacyPaymentsEntity").default,
    };
  }

  // XXX: RULE 10, ruled 2026-08-27: code shall NEVER define the provided or
  // XXX: required contexts of a page or component. They live in the DC's root
  // XXX: options in the database and nowhere else. The one sanctioned exception
  // XXX: is a HINT that seeds a NEWLY CREATED page -- consulted once, written to
  // XXX: the root options, and author-owned from then on (so deleting it makes it
  // XXX: stay deleted). Anything re-read from code on every load is a definition,
  // XXX: not a hint, and is forbidden.
  // XXX: The per-class `static requiredContexts` / `static providesContexts`
  // XXX: declarations are GONE from EVERY class in this directory, not just this
  // XXX: one -- three subclasses kept theirs for months and base-dynamic-page
  // XXX: made providesContexts an abstract-method CONTRACT demanding the exact
  // XXX: opposite of the rule. tests/unit/builder/elements/standard-elements-are-always-in-the-palette.test.mjs now
  // XXX: enumerates the whole directory so a new subclass cannot reintroduce one.
  // XXX: Also gone: getRequiredContexts. A DC's OWN
  // XXX: contexts live in its stored root options and nowhere else -- a
  // XXX: code-side copy is a parallel source of truth an author can neither
  // XXX: see the origin of nor edit, which is how a seeded LegacyUserProfile
  // XXX: requirement ended up frozen in the inspector. Read them with
  // XXX: readRequiredContexts / readProvidesContexts, which read the options.
  // XXX:
  // XXX: standardElementEntities above is a DIFFERENT thing and MUST stay.
  static getStandardElementEntities() {
    return { ...this.standardElementEntities };
  }


  static #baseProductionElementsCache = null;

  static get baseProductionElements() {
    if (BaseDynamicComponent.#baseProductionElementsCache === null) {
      BaseDynamicComponent.#baseProductionElementsCache = {
        [require("%/dynamic-component-editor/standard-elements/layouts/VerticalContainer.vue").default.name]: require("%/dynamic-component-editor/standard-elements/layouts/VerticalContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/layouts/HorizontalContainer.vue").default.name]: require("%/dynamic-component-editor/standard-elements/layouts/HorizontalContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/layouts/CarouselContainer.vue").default.name]: require("%/dynamic-component-editor/standard-elements/layouts/CarouselContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/layouts/Container.vue").default.name]: require("%/dynamic-component-editor/standard-elements/layouts/ContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/link/LinkContainer.vue").default.name]: require("%/dynamic-component-editor/standard-elements/link/LinkContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/entity-action/EntityActionContainer.vue").default.name]: require("%/dynamic-component-editor/standard-elements/entity-action/EntityActionContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/file-upload/FileUploadContainer.vue").default.name]: require("%/dynamic-component-editor/standard-elements/file-upload/FileUploadContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/entity-modal/EntityModalContainer.vue").default.name]: require("%/dynamic-component-editor/standard-elements/entity-modal/EntityModalContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/hover/HoverAreaContainer.vue").default.name]: require("%/dynamic-component-editor/standard-elements/hover/HoverAreaContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/hover/HoverVisibleContainer.vue").default.name]: require("%/dynamic-component-editor/standard-elements/hover/HoverVisibleContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/dropdown-button/DropdownButtonContainer.vue").default.name]: require("%/dynamic-component-editor/standard-elements/dropdown-button/DropdownButtonContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-components/plan-sheet/SubscriptionPlanSheet.vue").default.name]: require("%/dynamic-component-editor/standard-components/plan-sheet/SubscriptionPlanSheetProduction.vue").default,
        // Wrappers = Container + a fixed static-layout-direction option, which ContainerProduction honors.
        [require("%/dynamic-component-editor/standard-elements/layouts/VerticalContainerWrapper.vue").default.name]: require("%/dynamic-component-editor/standard-elements/layouts/ContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/layouts/HorizontalContainerWrapper.vue").default.name]: require("%/dynamic-component-editor/standard-elements/layouts/ContainerProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/text-element/TextElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/text-element/TextElementProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/text-element/BrandedTextElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/text-element/BrandedTextElementProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/text-element/BrandedTitleElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/text-element/BrandedTitleElementProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/text-element/BrandedHeadingElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/text-element/BrandedHeadingElementProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/text-element/BrandedSubHeadingElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/text-element/BrandedSubHeadingElementProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/text-element/BrandedParagraphElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/text-element/BrandedParagraphElementProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/text-element/BrandedLabelElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/text-element/BrandedLabelElementProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/text-element/BrandedNoteElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/text-element/BrandedNoteElementProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/button-element/ButtonElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/button-element/ButtonElementProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/url-element/UrlElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/url-element/UrlElementProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/image-element/ImageElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/image-element/ImageElementProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/video-element/VideoElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/video-element/VideoElementProduction.vue").default,
        [require("%/dynamic-component-editor/standard-elements/custom-html-element/CustomHtmlElement.vue").default.name]: require("%/dynamic-component-editor/standard-elements/custom-html-element/CustomHtmlElementProduction.vue").default,
        // The production twin lives in FanWeb (the video stack must not reach
        // the builder bundle); this map is the only place it can win over the
        // editor twin, since every other layer resolves the shared name.
        [require("%/dynamic-component-editor/standard-pages/event-page/EventVideoRoom.vue").default.name]: require("~/dynamic-components/standard-pages/event-page/EventVideoRoom.vue").default,
      };
    }
    return BaseDynamicComponent.#baseProductionElementsCache;
  }

  static #baseElementsCache = null;

  static get baseElements() {
    if (BaseDynamicComponent.#baseElementsCache === null) {
      BaseDynamicComponent.#baseElementsCache = [
        require("%/dynamic-component-editor/standard-elements/layouts/VerticalContainer.vue").default,
        require("%/dynamic-component-editor/standard-elements/layouts/HorizontalContainer.vue").default,
        require("%/dynamic-component-editor/standard-elements/layouts/CarouselContainer.vue").default,
        require("%/dynamic-component-editor/standard-elements/layouts/Container.vue").default,
        require("%/dynamic-component-editor/standard-elements/image-element/ImageElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/video-element/VideoElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/button-element/ButtonElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/text-element/TextElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/url-element/UrlElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/spacer-element/SpacerElement.vue").default,
        // BrandedTextElement is the base class every BrandedXxxElement
        // extends. It's hidden from the elements panel via its own
        // `hidden: true`, but kept registered here so any pre-existing
        // entity persisted as `type: "BrandedTextElement"` (from an earlier
        // experiment) still resolves to a Vue component instead of failing
        // the renderer's missing-type guard.
        require("%/dynamic-component-editor/standard-elements/text-element/BrandedTextElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/text-element/BrandedHeadingElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/text-element/BrandedSubHeadingElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/text-element/BrandedTitleElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/text-element/BrandedLabelElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/text-element/BrandedParagraphElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/text-element/BrandedNoteElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/AdUnit.vue").default,
        require("%/dynamic-component-editor/standard-elements/Icon.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-component-element/DynamicComponentElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/custom-html-element/CustomHtmlElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/hover/HoverAreaContainer.vue").default,
        require("%/dynamic-component-editor/standard-elements/hover/HoverVisibleContainer.vue").default,
        require("%/dynamic-component-editor/standard-elements/dropdown-button/DropdownButtonContainer.vue").default,
      ];
    }
    return BaseDynamicComponent.#baseElementsCache;
  }

  static #elementsWithContextCache = null;

  static get elementsWithContext() {
    if (BaseDynamicComponent.#elementsWithContextCache === null) {
      BaseDynamicComponent.#elementsWithContextCache = [
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/DynamicAppTextElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/DynamicAppLinkElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/DynamicAppMarkdownElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/DynamicAppTagListElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/DynamicAppDateElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/DynamicAppFeedElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/FeedV2Element.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/DynamicAppComponentElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/DynamicAppImageElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/DynamicAppVideoElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/DynamicAppButtonElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/DynamicAppStoryElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/DynamicAppStoryInputElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/DynamicAppRichTextInputElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/DynamicAppTextInputElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/DynamicAppTextAreaInputElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/DynamicAppNumberInputElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/DynamicAppCheckboxInputElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/DynamicAppEnumInputElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/DynamicAppDateInputElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/DynamicAppVideoInputElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/DynamicAppImageInputElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/DynamicAppFileInputElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/DynamicAppSelectInputElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/link/LinkContainer.vue").default,
        require("%/dynamic-component-editor/standard-elements/entity-action/EntityActionContainer.vue").default,
        require("%/dynamic-component-editor/standard-elements/file-upload/FileUploadContainer.vue").default,
        require("%/dynamic-component-editor/standard-elements/entity-modal/EntityModalContainer.vue").default,
        // Host call elements bound to a Chat Conversation: listed here so a
        // published page resolves them (its palette entries come from
        // app-entity-extensions.js).
        require("%/dynamic-component-editor/standard-elements/conversation-call/ConversationCallStatus.vue").default,
        require("%/dynamic-component-editor/standard-elements/conversation-call/ConversationCallParticipantCount.vue").default,
        require("%/dynamic-component-editor/standard-elements/conversation-call/ConversationJoinCallButton.vue").default,
        require("%/dynamic-component-editor/standard-elements/conversation-call/ConversationVideoCall.vue").default,
        require("%/dynamic-component-editor/standard-elements/call-room/CallRoomElement.vue").default,
        require("%/dynamic-component-editor/standard-elements/story-composer/StoryComposerElement.vue").default,
      ];
    }
    return BaseDynamicComponent.#elementsWithContextCache;
  }

  static get standardElementVueComponents() {
    return [
      ...this.baseElements,
      ...this.elementsWithContext,
    ];
  }

  static standardOptionClasses = [
    new ComponentName("", true),
    new DisplayName("", true),
    ComponentDescription,
    RequiredContexts,
  ];

  static possibleElementVueComponents = [];
  static possibleOptionClasses = [];

  constructor() {
    if (!Object.prototype.hasOwnProperty.call(this.constructor, "getPossibleElements")) {
      throw new DynamicComponentError("getPossibleElements is not defined in component", {
        componentName: this.name,
      });
    }
  }

  static collectMixins(comp) {
    const allMixins = [];
    if (comp.mixins && Array.isArray(comp.mixins)) {
      comp.mixins.forEach(mixin => {
        this.collectMixins(mixin);
        allMixins.push(mixin);
      });
    }

    return allMixins;
  }

  static getAllMixins(component) {
    const allMixins = [];
    allMixins.push(...this.collectMixins(component));
    return allMixins;
  }

  static processElementVueComponents(elementVueComponents) {

    elementVueComponents.forEach(component => {
      if (!elementVueComponentRegistry.shouldProcess(component)) {
        return;
      }
      let elementPanelSettings = {};
      let options = [];
      let optionBundles = [];
      let availableStates = [...Object.values(BaseOption.$states)];
      let optionsOrder = ["lock-options", "appearance", "animations"];
      let brandingType = null;

      const extendedComponent = component.extends;

      if (extendedComponent) {
        elementPanelSettings = {
          ...elementPanelSettings,
          ...extendedComponent.elementPanelSettings,
        };

        const allMixins = this.getAllMixins(extendedComponent).reverse();
        allMixins.forEach(mixin => {
          elementPanelSettings = {
            ...elementPanelSettings,
            ...mixin.elementPanelSettings ?? {},
          };

          options = mergeArrayOfClassInstances(options, mixin.options ?? []);
          optionBundles = mergeArrayOfClassInstances(optionBundles, mixin.optionBundles ?? []);
          optionsOrder = mergeArrayOfClassInstances(optionsOrder, mixin.optionsOrder ?? []);
          availableStates = mergeArrayOfClassInstances(availableStates, mixin.availableStates ?? []);
          brandingType = brandingType ?? mixin.brandingType;
        });
      }


      const allMixins = this.getAllMixins(component).reverse();
      allMixins.forEach(mixin => {
        elementPanelSettings = {
          ...elementPanelSettings,
          ...mixin.elementPanelSettings ?? {},
        };

        options = mergeArrayOfClassInstances(options, mixin.options ?? []);
        optionBundles = mergeArrayOfClassInstances(optionBundles, mixin.optionBundles ?? []);
        optionsOrder = mergeArrayOfClassInstances(optionsOrder, mixin.optionsOrder ?? []);
        availableStates = mergeArrayOfClassInstances(availableStates, mixin.availableStates ?? []);
        brandingType = brandingType ?? mixin.brandingType;
      });

      elementPanelSettings = {
        ...elementPanelSettings,
        ...component.elementPanelSettings ?? {},
      };

      options = mergeArrayOfClassInstances(options, component.options ?? []);
      optionBundles = mergeArrayOfClassInstances(optionBundles, component.optionBundles ?? []);
      optionsOrder = mergeArrayOfClassInstances(optionsOrder, component.optionsOrder ?? []);
      availableStates = mergeArrayOfClassInstances(availableStates, component.availableStates ?? []);
      brandingType = brandingType ?? component.brandingType;

      component.elementPanelSettings = elementPanelSettings;
      component.options = options;
      component.optionBundles = optionBundles;
      component.optionsOrder = optionsOrder;
      component.availableStates = availableStates;
      component.brandingType = brandingType;
    });

    return elementVueComponents;
  }

  static getPossibleElements() {
    let possibleElementVueComponents = this.possibleElementVueComponents;
    if (!Array.isArray(this.possibleElementVueComponents)) {
      possibleElementVueComponents = [];
    }

    const elementVueComponents = [
      ...this.standardElementVueComponents,
      ...possibleElementVueComponents,
    ];

    return this.processElementVueComponents(elementVueComponents);
  }

  static getPossibleElementTypes() {
    return [
      ...this.standardElementVueComponents,
      ...this.possibleElementVueComponents,
    ].map(element => {
      return element.name;
    });
  }

  static getProductionElementVueComponent(elementComponentName) {
    const foundProductionReplacementKey = Object.keys(this.baseProductionElements).find(elementName => {
      return elementName === elementComponentName;
    });

    if (foundProductionReplacementKey) {
      return this.baseProductionElements[foundProductionReplacementKey];
    }

    return this.getElementVueComponent(elementComponentName);
  }

  static getElementVueComponent(elementComponentName) {
    const elementVueComponent = this.getPossibleElements().find(elementComponent => {
      return elementComponent.name === elementComponentName;
    });

    if (!elementVueComponent) {
      return null;
    }
    return elementVueComponent;
  }

  static getOptions() {
    if (!Array.isArray(this.possibleOptionClasses)) {
      throw new DynamicComponentError("possibleOptionClasses must be defined as an array in dynamic component", {
        possibleOptionClasses: this.possibleOptionClasses,
        componentName: this.name,
      });
    }

    return [
      ...this.standardOptionClasses,
      ...this.possibleOptionClasses,
    ];
  }
}
